import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Paper, Divider, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePTagForm } from "../contexts/PTagFormContext";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getTagInfoforWhatsAppLink } from "../services/api";
import { version } from "dompurify";

export default function PTagAirConfirmation() {
  const { form, setForm, forms, removeAllForms, orderConfirmed } = usePTagForm();
  const shipping = 50;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const _id = searchParams.get('qr') || '';

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Fetch data for whatsapp link
  useEffect(() => {
    if (_id) {
      setLoading(true);
      getTagInfoforWhatsAppLink(_id)
        .then((response) => {
          const tagData = response.data.form;

          setForm((prev) => ({
            ...prev,
            petName: tagData.petName ?? prev.petName,
            phoneNumber: tagData.contact ?? prev.phoneNumber,
            email: tagData.masterEmail ?? prev.email,
            photo: tagData.petUrl ?? prev.photo,
            lastName: tagData.petHuman ?? prev.lastName,
            shippingAddress: tagData.location ?? prev.shippingAddress,
            tempId: tagData.orderId ?? prev.tempId,
            pendingStatus: tagData.pendingStatus ?? prev.pendingStatus,
            price: tagData.price ?? prev.price,
            option: tagData.option ?? prev.option,
            version: tagData.type ?? prev.version,
            size: tagData.optionSize ?? prev.size,
            color: tagData.optionColor ?? prev.color,
          }));
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          console.error("Error fetching Tag Info:", error);
        });
    }
  }, [_id]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = windowWidth < 768;

  if (loading) {
    return (
      <Box
        sx={{
          bgcolor: "#fcf8f3",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {/* 載入中... */}
            <img src="https://petpetclub.s3.ap-southeast-1.amazonaws.com/user-uploads/pets/68cb893a0feda12f2dbcc02b/68d4c8ce22baedfbe8b852dd.gif" alt=""
              style={{
                width: '150px',
                height: 'auto',
                mixBlendMode: 'multiply',
              }} />
            <div
              style={{
                fontWeight: "500px",
                fontSize: "15px",
                textAlign: "center",
                fontFamily: "Lucida Console",

              }}>
              {t('Loading')}
            </div>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{ background: "#FCF8F3", minHeight: "100vh", pt: { xs: 6, md: 10 } }}
    >
      <Navbar />
      {/* Success message */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          pt: 5,
        }}
      >
        <Box sx={{ mr: isMobile ? 0 : 4, width: isMobile ? "15%" : "auto", mb: isMobile ? 2 : 0 }}>
          <img src="\icons\check-circle.png" alt="" width={"100%"} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "center" : "flex-start", }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              mb: 1,
              fontSize: { xs: 16, sm: 24, md: 28 },
              textAlign: { xs: "left", md: "left" },
            }}
          >
            {t("Thankyou")} {t("confirmed")}
          </Typography>
          <Typography
            fontSize={15}
            sx={{ mb: 0.5, textAlign: { xs: "left", md: "left" } }}
          >
            {t("OrderNumber")} {forms.map(f => f.tempId).join(",")}
          </Typography>
          <Typography
            fontSize={15}
            sx={{ mb: 2, textAlign: { xs: "center", md: "left" } }}
          >
            {t("OrderInfo")} {forms[0].email || "-"}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          maxWidth: 1080,
          width: "100%",
          mx: "auto",
          px: { xs: 1, sm: 2 },
          pt: { xs: 1, md: 3 },
          pb: { xs: 4, md: 8 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 4, md: 6 },
            width: "100%",
          }}
        >
          {/* Left: Order Summary */}
          <Box sx={{ flex: 1, minWidth: 0, mb: { xs: 4, md: 0 }, px: 1 }}>
            <>
              {/* Subtotal */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, pb: 1 }}>
                <Typography>{t('subtotal')}</Typography>
                <Typography fontWeight={600}>
                  HKD ${forms.reduce((sum, f) => sum + (f.price || 0), 0)}
                </Typography>
              </Box>

              {/* Shipping */}
              <Box sx={{ display: "flex", justifyContent: "space-between", pb: 2 }}>
                <Typography>{t('shippingFee')}</Typography>
                <Typography fontWeight={600}>HKD ${50 * (forms.length)}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Grand Total */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="bold">
                  {t('total')}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="#FFB60C">
                  HKD ${forms.reduce((sum, f) => sum + (f.price || 0), 0) + 50 * (forms.length)}
                </Typography>
              </Box>
            </>
            <Box
              sx={{
                mt: 3,
                fontSize: 15,
                color: "#222",
                width: "100%",
              }}
            >
              <Typography sx={{ mb: 1, fontWeight: 700 }}>
                {t('ExtraInfo1')}
                <br />
                {t('ExtraInfo2')}）
              </Typography>
              <Typography sx={{ mb: 1, mt: 2 }}>
                {t('ExtraInfo3')}
                <br />
                {t('ExtraInfo4')}
                <br />
                Pet Pet Club Limited
              </Typography>
              <Typography sx={{ mb: 1, fontWeight: 700, mt: 2 }}>
                {t('ExtraInfo5')}
                <br />
                {t('ExtraInfo6')}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                WhatsApp：5988 4711 / 5576 4375
                <br />
                Email：support@ptag.com.hk
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography>
                {t('ExtraInfo7')}
                <br />
                WhatsApp：5988 4711 / 5576 4375
              </Typography>
            </Box>
          </Box>
          {/* Vertical Divider for desktop only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, mx: 3, minHeight: 400 }}
          />
          {/* Right: Order Details */}
          <Box
            sx={{ flex: 1, minWidth: 0, width: "100%", mt: { xs: 4, md: 0 }, px: 1 }}
          >
            <Paper
              elevation={0}
              sx={{
                background: "#FCF8F3",
                minHeight: 400,
                width: "100%",
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                {t('OrderDetails')}
              </Typography>
              {forms.map((f, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    {t('PtagAirPrintContent')}
                  </Typography>

                  <Typography>{f.petName}</Typography>
                  <Typography>{`${f.option} • ${f.tempId}`}</Typography>
                  <Typography>{f.petContact}</Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 350,
                      minHeight: 120,
                      background: "#fff",
                      border: "1px solid #eee",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    {f.photo ? (
                      <img
                        src={
                          typeof f.photo === "string"
                            ? f.photo
                            : URL.createObjectURL(f.photo)
                        }
                        alt="pet print"
                        style={{
                          width: "100%",
                          maxHeight: 200,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          minHeight: 120,
                          background: "#fff",
                          border: "1px dashed #ccc",
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="73" height="69" viewBox="0 0 73 69" fill="none">
                          <ellipse cx="11.7839" cy="35.8738" rx="7.37667" ry="10.9231" transform="rotate(-29.3518 11.7839 35.8738)" fill="#D9D9D9" />
                          <ellipse cx="25.6509" cy="12.0045" rx="7.37667" ry="10.9231" transform="rotate(-9.72478 25.6509 12.0045)" fill="#D9D9D9" />
                          <ellipse cx="7.37667" cy="10.9231" rx="7.37667" ry="10.9231" transform="matrix(-0.871627 -0.49017 -0.49017 0.871627 72.9961 29.9688)" fill="#D9D9D9" />
                          <ellipse cx="7.37667" cy="10.9231" rx="7.37667" ry="10.9231" transform="matrix(-0.985631 -0.168916 -0.168916 0.985631 56.4648 2.48438)" fill="#D9D9D9" />
                          <path d="M36.1553 35.2969C40.7313 35.2969 44.5896 38.3576 45.8018 42.543L52.3516 53.1035C55.2902 57.8413 53.8323 64.0641 49.0947 67.0029C44.892 69.6098 39.5225 68.7546 36.3115 65.2217C33.1006 68.7543 27.7319 69.6095 23.5293 67.0029C18.7916 64.0642 17.333 57.8413 20.2715 53.1035L26.3027 43.3779C27.2159 38.7705 31.2798 35.2969 36.1553 35.2969Z" fill="#D9D9D9" />
                        </svg>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
              <Box sx={{ mb: 3 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  {t('deliveryInfo')}
                </Typography>
                <Typography>{forms[0].lastName || "-"}</Typography>
                <Typography>{forms[0].phoneNumber || "-"}</Typography>
                <Typography>{forms[0].email || "-"}</Typography>
                <Typography>
                  {t('Address')}：
                  {`${forms[0].address || forms[0].shippingAddress || "-"}`}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  {t('paymentInfo')}
                </Typography>
                <Typography>
                  {forms[0].pendingStatus ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <img
                        src="/icons/checkGif.gif"
                        alt="Success"
                        style={{
                          width: "40px",
                          height: "auto",
                          mixBlendMode: "multiply",
                        }}
                      />
                      <span style={{ marginLeft: 8 }}>{t('Paid')}</span>
                    </span>
                  ) : (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <img
                        src="/icons/pending.gif"
                        alt="Success"
                        style={{
                          width: "80px",
                          height: "auto",
                          mixBlendMode: "multiply",
                          margin: "-20px",
                        }}
                      />
                      <span style={{ marginLeft: 8 }}>{t('Pending')}</span>
                    </span>

                  )}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center", pt: isMobile ? 0 : 4 }}>
          <Button
            variant="contained"
            sx={{
              background: "#FFB60C",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              px: 6,
              py: 1.5,
              mt: 2,
              boxShadow: "none",
              width: { xs: "100%", md: "auto" },
              "&:hover": { background: "#FFB60C" },
            }}
            fullWidth={true}
            onClick={() => { removeAllForms(); window.location.href = "/"; }}
          >
            {t('BackToHome')}
          </Button>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}
